<template>
  <div class="status-banners-container">
    <!-- Bannière mode hors ligne -->
    <div v-if="isUserOfflineMode" class="status-banner offline-banner">
      <i class="pi pi-info-circle banner-icon"></i>
      <div class="banner-content">
        <div class="banner-title">Mode hors ligne activé</div>
        <div class="banner-description">Les données affichées sont celles disponibles en local. Les modifications seront synchronisées quand vous passerez en mode en ligne.</div>
      </div>
    </div>

    <!-- Bannière connexion perdue -->
    <div v-else-if="!isOnline" class="status-banner connection-lost-banner">
      <i class="pi pi-exclamation-triangle banner-icon"></i>
      <div class="banner-content">
        <div class="banner-title">Connexion internet perdue</div>
        <div class="banner-description">Fonctionnement en mode hors ligne temporaire. Les données sont sauvegardées localement.</div>
      </div>
    </div>

    <!-- Bannière erreurs de synchronisation -->
    <div v-if="hasManualSyncNeeded" class="status-banner sync-error-banner">
      <i class="pi pi-exclamation-circle banner-icon"></i>
      <div class="banner-content">
        <div class="banner-title">Erreurs de synchronisation</div>
        <div class="banner-description">Certaines modifications n'ont pas pu être synchronisées après plusieurs tentatives.</div>
        <button class="banner-action-button" @click="$emit('show-sync-error-details')">
          <i class="pi pi-eye"></i>
          <span>Voir les détails</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
// Props
const props = defineProps({
  isUserOfflineMode: {
    type: Boolean,
    default: false
  },
  isOnline: {
    type: Boolean,
    default: true
  },
  hasManualSyncNeeded: {
    type: Boolean,
    default: false
  }
});

// Emit events
const emit = defineEmits(['show-sync-error-details']);
</script>

<style scoped>
.status-banners-container {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.status-banner {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  animation: fadeIn 0.3s ease;
  position: relative;
}

.banner-icon {
  font-size: 1.1rem;
  flex-shrink: 0;
  margin-top: 0.15rem;
}

.banner-content {
  flex: 1;
}

.banner-title {
  font-weight: 600;
  font-size: 0.95rem;
  margin-bottom: 0.25rem;
}

.banner-description {
  font-size: 0.85rem;
  line-height: 1.4;
  opacity: 0.9;
}

.banner-action-button {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.75rem;
  padding: 0.35rem 0.75rem;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 500;
  transition: all 0.2s ease;
  background-color: rgba(255, 255, 255, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.4);
}

.banner-action-button:hover {
  background-color: rgba(255, 255, 255, 0.4);
}

/* Styles spécifiques par type de bannière */
.offline-banner {
  background-color: #FFFBEB;
  border: 1px solid #FEF3C7;
  color: #92400E;
}

.offline-banner .banner-icon {
  color: #F59E0B;
}

.connection-lost-banner {
  background-color: #FEF2F2;
  border: 1px solid #FEE2E2;
  color: #B91C1C;
}

.connection-lost-banner .banner-icon {
  color: #EF4444;
}

.sync-error-banner {
  background-color: #FEF2F2;
  border: 1px solid #FEE2E2;
  color: #B91C1C;
}

.sync-error-banner .banner-icon {
  color: #EF4444;
}

.sync-error-banner .banner-action-button {
  border-color: rgba(239, 68, 68, 0.3);
  color: #B91C1C;
}

.sync-error-banner .banner-action-button:hover {
  background-color: rgba(239, 68, 68, 0.1);
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style> 